import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { Modal } from '@/components/ui/Modal'
import { useDevis, useDevisById, useDevisNumero } from '@/hooks/useDevis'
import { useContacts } from '@/hooks/useContacts'
import { supabase } from '@/lib/supabase'
import { formatCurrency, generateId } from '@/lib/utils'
import { calculateTotalHT, calculateTVA, calculateTotalTTC } from '@/services/devis'
import type { LigneDevis, DevisFormData } from '@/types/devis'
import { ProductSelect } from '@/components/admin/ProductSelect'
import type { Produit } from '@/types/database-real'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Send,
  Eye,
  User,
  Search,
  Building2,
  Mail,
} from 'lucide-react'

interface ContactSelectModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (contactId: string) => void
}

function ContactSelectModal({ isOpen, onClose, onSelect }: ContactSelectModalProps) {
  const [search, setSearch] = useState('')
  const { contacts, isLoading } = useContacts({ search })

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Selectionner un client" size="lg">
      <div className="space-y-4">
        <Input
          placeholder="Rechercher un contact..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-brown-500">
            <User className="w-8 h-8 mx-auto mb-2 text-beige-300" />
            <p>Aucun contact trouve</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2">
            {contacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => {
                  onSelect(contact.id)
                  onClose()
                }}
                className="w-full p-3 rounded-lg border border-beige-200 hover:border-mint-500 hover:bg-mint-50 transition-colors text-left flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                  <span className="text-mint-700 font-semibold">
                    {contact.prenom?.charAt(0) || ''}{contact.nom?.charAt(0) || ''}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-brown-900">
                    {contact.prenom} {contact.nom}
                  </div>
                  <div className="text-sm text-brown-500 flex items-center gap-2">
                    {contact.entreprise && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {contact.entreprise}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {contact.email}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default function NewQuotePage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const { createDevis, updateDevis, sendDevis, isCreating, isUpdating, isSending } = useDevis()
  const { data: existingDevis, isLoading: isLoadingDevis } = useDevisById(id || '')
  const { data: generatedNumero } = useDevisNumero()
  const { contacts } = useContacts()

  const [showContactModal, setShowContactModal] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Form state
  const [formData, setFormData] = useState({
    numero: '',
    contact_id: '',
    projet_id: '',
    titre: '',
    description: '',
    date_emission: new Date().toISOString().split('T')[0],
    validite_jours: 30,
    taux_tva: 0,
    conditions: 'Devis valable 30 jours. Paiement a 30 jours apres facturation.',
    notes_internes: '',
  })

  // Charger les projets du client sélectionné
  const [clientProjects, setClientProjects] = useState<{ id: string; title: string }[]>([])
  useEffect(() => {
    if (!formData.contact_id) {
      setClientProjects([])
      setFormData(prev => ({ ...prev, projet_id: '' }))
      return
    }
    supabase
      .from('projects')
      .select('id, title')
      .eq('user_id', formData.contact_id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setClientProjects(data || []))
  }, [formData.contact_id])

  const [lignes, setLignes] = useState<LigneDevis[]>([
    {
      id: generateId(),
      description: '',
      quantite: 1,
      prix_unitaire: 0,
      taux_tva: 0,
      total_ht: 0,
    },
  ])

  // Load existing devis for edit mode
  useEffect(() => {
    if (isEditMode && existingDevis) {
      setFormData({
        numero: existingDevis.numero,
        contact_id: existingDevis.contact_id,
        titre: existingDevis.titre || '',
        description: existingDevis.description || '',
        date_emission: existingDevis.date_emission,
        validite_jours: existingDevis.validite_jours || 30,
        taux_tva: existingDevis.taux_tva ?? 20,
        conditions: existingDevis.conditions || '',
        notes_internes: existingDevis.notes_internes || '',
      })
      if (existingDevis.lignes && existingDevis.lignes.length > 0) {
        setLignes(existingDevis.lignes)
      }
    }
  }, [isEditMode, existingDevis])

  // Set generated numero for new devis
  useEffect(() => {
    if (!isEditMode && generatedNumero) {
      setFormData((prev) => ({ ...prev, numero: generatedNumero }))
    }
  }, [isEditMode, generatedNumero])

  // Calculate date_validite based on validite_jours
  const dateValidite = useMemo(() => {
    const date = new Date(formData.date_emission)
    date.setDate(date.getDate() + formData.validite_jours)
    return date.toISOString().split('T')[0]
  }, [formData.date_emission, formData.validite_jours])

  // Calculate totals
  const totals = useMemo(() => {
    const totalHT = calculateTotalHT(lignes)
    const tva = calculateTVA(lignes, formData.taux_tva)
    const totalTTC = calculateTotalTTC(lignes, formData.taux_tva)
    return { totalHT, tva, totalTTC }
  }, [lignes, formData.taux_tva])

  // Get selected contact
  const selectedContact = useMemo(() => {
    return contacts.find((c) => c.id === formData.contact_id)
  }, [contacts, formData.contact_id])

  const updateLigne = (index: number, field: keyof LigneDevis, value: number | string) => {
    setLignes((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      // Recalculate total_ht
      updated[index].total_ht = updated[index].quantite * updated[index].prix_unitaire
      return updated
    })
  }

  // Select a product for a line
  const handleProductSelect = (index: number, produit: Produit | null) => {
    if (!produit) return

    setLignes((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        description: produit.nom + (produit.description ? ` - ${produit.description}` : ''),
        prix_unitaire: produit.prix_unitaire,
        taux_tva: produit.tva,
        total_ht: updated[index].quantite * produit.prix_unitaire,
      }
      return updated
    })
  }

  const addLigne = () => {
    setLignes((prev) => [
      ...prev,
      {
        id: generateId(),
        description: '',
        quantite: 1,
        prix_unitaire: 0,
        taux_tva: formData.taux_tva,
        total_ht: 0,
      },
    ])
  }

  const removeLigne = (index: number) => {
    if (lignes.length > 1) {
      setLignes((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.contact_id) {
      newErrors.contact_id = 'Veuillez selectionner un client'
    }
    if (!formData.titre.trim()) {
      newErrors.titre = 'Le titre est requis'
    }
    if (lignes.every((l) => !l.description.trim())) {
      newErrors.lignes = 'Ajoutez au moins une ligne avec une description'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (sendAfter: boolean = false) => {
    if (!validateForm()) return

    const devisData: DevisFormData = {
      numero: formData.numero,
      contact_id: formData.contact_id,
      projet_id: formData.projet_id || undefined,
      titre: formData.titre,
      description: formData.description || null,
      date_emission: formData.date_emission,
      date_validite: dateValidite,
      validite_jours: formData.validite_jours,
      status: 'draft',
      lignes: lignes.filter((l) => l.description.trim()),
      montant_ht: totals.totalHT,
      taux_tva: formData.taux_tva,
      montant_tva: totals.tva,
      montant_ttc: totals.totalTTC,
      conditions: formData.conditions || null,
      notes_internes: formData.notes_internes || null,
    }

    try {
      let savedDevis
      if (isEditMode && id) {
        savedDevis = await updateDevis({ id, data: devisData })
      } else {
        savedDevis = await createDevis(devisData)
      }

      if (sendAfter && savedDevis) {
        await sendDevis(savedDevis.id)
      }

      navigate('/devis')
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    }
  }

  if (isEditMode && isLoadingDevis) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/devis')}
          className="p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold text-brown-900">
            {isEditMode ? 'Modifier le devis' : 'Nouveau devis'}
          </h1>
          <p className="mt-1 text-brown-600">
            {formData.numero || 'Numero en cours de generation...'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            leftIcon={<Eye className="w-4 h-4" />}
          >
            Apercu
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSubmit(false)}
            isLoading={isCreating || isUpdating}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Enregistrer
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            isLoading={isSending}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Envoyer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client selection */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Client</CardTitle>
            </CardHeader>
            {selectedContact ? (
              <div className="flex items-center justify-between p-4 bg-beige-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                    <span className="text-mint-700 font-semibold">
                      {selectedContact.prenom?.charAt(0) || ''}{selectedContact.nom?.charAt(0) || ''}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-brown-900">
                      {selectedContact.prenom} {selectedContact.nom}
                    </div>
                    <div className="text-sm text-brown-500">
                      {selectedContact.email}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowContactModal(true)}
                >
                  Changer
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  variant="outline"
                  onClick={() => setShowContactModal(true)}
                  leftIcon={<User className="w-4 h-4" />}
                  className="w-full"
                >
                  Selectionner un client
                </Button>
                {errors.contact_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.contact_id}</p>
                )}
              </div>
            )}
          </Card>

          {/* Projet associe */}
          {formData.contact_id && clientProjects.length > 0 && (
            <Card padding="md">
              <CardHeader>
                <CardTitle>Projet associe</CardTitle>
              </CardHeader>
              <select
                value={formData.projet_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, projet_id: e.target.value }))}
                className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              >
                <option value="">Aucun projet</option>
                {clientProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </Card>
          )}

          {/* Quote details */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Details du devis</CardTitle>
            </CardHeader>
            <div className="space-y-4">
              <Input
                label="Titre du devis"
                value={formData.titre}
                onChange={(e) => setFormData((prev) => ({ ...prev, titre: e.target.value }))}
                placeholder="Ex: Creation site web vitrine"
                error={errors.titre}
              />

              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Description / Introduction
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Presentation du projet et des prestations proposees..."
                  rows={3}
                  className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                />
              </div>
            </div>
          </Card>

          {/* Lines */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Prestations</CardTitle>
            </CardHeader>
            <div className="space-y-4">
              {lignes.map((ligne, index) => (
                <div
                  key={ligne.id}
                  className="p-4 bg-beige-50 rounded-lg space-y-3"
                >
                  {/* Product Select */}
                  <ProductSelect
                    value={undefined}
                    onChange={(produit) => handleProductSelect(index, produit)}
                  />

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      Description (modifiable)
                    </label>
                    <Input
                      placeholder="Description de la prestation"
                      value={ligne.description}
                      onChange={(e) => updateLigne(index, 'description', e.target.value)}
                    />
                  </div>

                  {/* Quantity, Price, Total */}
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="Quantite"
                      type="number"
                      min={1}
                      value={ligne.quantite}
                      onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                    />
                    <Input
                      label="Prix unitaire HT"
                      type="number"
                      min={0}
                      step={0.01}
                      value={ligne.prix_unitaire}
                      onChange={(e) => updateLigne(index, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                    />
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        Total HT
                      </label>
                      <div className="px-4 py-2 bg-white border border-beige-200 rounded-lg text-brown-900 font-medium">
                        {formatCurrency(ligne.total_ht)}
                      </div>
                    </div>
                  </div>

                  {/* Delete button */}
                  {lignes.length > 1 && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => removeLigne(index)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer cette ligne
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {errors.lignes && (
                <p className="text-sm text-red-600">{errors.lignes}</p>
              )}

              <Button
                variant="outline"
                onClick={addLigne}
                leftIcon={<Plus className="w-4 h-4" />}
                className="w-full"
              >
                Ajouter une prestation
              </Button>
            </div>
          </Card>

          {/* Conditions */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Conditions</CardTitle>
            </CardHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Conditions d'acceptation
                </label>
                <textarea
                  value={formData.conditions}
                  onChange={(e) => setFormData((prev) => ({ ...prev, conditions: e.target.value }))}
                  placeholder="Conditions de paiement, modalites..."
                  rows={3}
                  className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Notes internes (non visibles par le client)
                </label>
                <textarea
                  value={formData.notes_internes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes_internes: e.target.value }))}
                  placeholder="Notes pour usage interne..."
                  rows={2}
                  className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Validity */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Validite</CardTitle>
            </CardHeader>
            <div className="space-y-4">
              <Input
                label="Date d'emission"
                type="date"
                value={formData.date_emission}
                onChange={(e) => setFormData((prev) => ({ ...prev, date_emission: e.target.value }))}
              />

              <Input
                label="Duree de validite (jours)"
                type="number"
                min={1}
                value={formData.validite_jours}
                onChange={(e) => setFormData((prev) => ({ ...prev, validite_jours: parseInt(e.target.value) || 30 }))}
              />

              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Date d'expiration
                </label>
                <div className="px-4 py-2 bg-beige-50 border border-beige-200 rounded-lg text-brown-600">
                  {new Date(dateValidite).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
            </div>
          </Card>

          {/* TVA */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>TVA</CardTitle>
            </CardHeader>
            <div className="space-y-3">
              <Input
                label="Taux de TVA (%)"
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={formData.taux_tva}
                onChange={(e) => setFormData((prev) => ({ ...prev, taux_tva: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </Card>

          {/* Totals */}
          <Card padding="md">
            <CardHeader>
              <CardTitle>Recapitulatif</CardTitle>
            </CardHeader>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-brown-500">Total HT</span>
                <span className="font-medium text-brown-900">
                  {formatCurrency(totals.totalHT)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brown-500">TVA ({formData.taux_tva}%)</span>
                <span className="font-medium text-brown-900">
                  {formatCurrency(totals.tva)}
                </span>
              </div>
              <div className="pt-3 border-t border-beige-200 flex justify-between">
                <span className="font-medium text-brown-900">Total TTC</span>
                <span className="text-xl font-bold text-mint-600">
                  {formatCurrency(totals.totalTTC)}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Contact selection modal */}
      <ContactSelectModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSelect={(contactId) => setFormData((prev) => ({ ...prev, contact_id: contactId }))}
      />

      {/* Preview modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Apercu du devis"
        size="xl"
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-start border-b border-beige-200 pb-4">
            <div>
              <h2 className="text-xl font-bold text-brown-900">LesSablesWeb</h2>
              <p className="text-sm text-brown-500">Agence Web & Marketing Digital</p>
            </div>
            <div className="text-right">
              <h3 className="text-lg font-bold">Devis {formData.numero}</h3>
              <p className="text-sm text-brown-500">
                Valide jusqu'au {new Date(dateValidite).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>

          {/* Client info */}
          {selectedContact && (
            <div className="bg-beige-50 p-4 rounded-lg">
              <p className="text-sm text-brown-500 mb-1">Client</p>
              <p className="font-medium text-brown-900">
                {selectedContact.prenom} {selectedContact.nom}
              </p>
              {selectedContact.entreprise && (
                <p className="text-brown-600">{selectedContact.entreprise}</p>
              )}
              <p className="text-sm text-brown-500">{selectedContact.email}</p>
            </div>
          )}

          {/* Title and description */}
          <div>
            <h4 className="text-lg font-semibold text-brown-900">{formData.titre}</h4>
            {formData.description && (
              <p className="mt-2 text-brown-600">{formData.description}</p>
            )}
          </div>

          {/* Lines table */}
          <table className="w-full">
            <thead className="bg-beige-100">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-brown-600">Description</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">Qte</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">Prix unit. HT</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {lignes.filter(l => l.description.trim()).map((ligne) => (
                <tr key={ligne.id} className="border-b border-beige-100">
                  <td className="px-4 py-3 text-brown-900">{ligne.description}</td>
                  <td className="px-4 py-3 text-right text-brown-600">{ligne.quantite}</td>
                  <td className="px-4 py-3 text-right text-brown-600">
                    {formatCurrency(ligne.prix_unitaire)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-brown-900">
                    {formatCurrency(ligne.total_ht)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span className="text-brown-500">Total HT</span>
                <span className="font-medium">{formatCurrency(totals.totalHT)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brown-500">TVA ({formData.taux_tva}%)</span>
                <span>{formatCurrency(totals.tva)}</span>
              </div>
              <div className="pt-2 border-t border-beige-200 flex justify-between text-lg font-bold">
                <span>Total TTC</span>
                <span className="text-mint-600">{formatCurrency(totals.totalTTC)}</span>
              </div>
            </div>
          </div>

          {/* Conditions */}
          {formData.conditions && (
            <div className="bg-beige-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-brown-700 mb-2">Conditions</p>
              <p className="text-sm text-brown-600">{formData.conditions}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}
